import { useMemo, type ReactNode } from 'react';
import {
  Area,
  ComposedChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCompactCurrency, formatCurrency } from '@budget/core';
import type { RetirementData, Tri } from '@budget/core';
import {
  dedupeSnapshotsByDate,
  useNetWorthHistory,
  type NetWorthSnapshot as Snapshot,
} from '../data/useNetWorthHistory';
import type { Scope } from './ScopeToggle';

const TOOLTIP_STYLE = {
  background: 'var(--berg-bg-elevated)',
  border: '1px solid var(--berg-border-strong)',
  borderRadius: 12,
  padding: '8px 12px',
  fontSize: 12,
  color: 'var(--berg-text)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
};
const TOOLTIP_LABEL_STYLE = { color: 'var(--berg-text)', fontWeight: 600 };

interface Point {
  // YYYY-MM-DD string — used for tooltip/label lookup and de-duping by
  // calendar day. The chart's X-axis is driven by `ts` (numeric, ms UTC
  // midnight) so points are spaced by actual elapsed time rather than by
  // categorical index — otherwise daily snapshots in dense periods
  // visually crowd into a tiny slice while sparse months stretch wide.
  date: string;
  ts: number;
  // Only one of these is populated per point — the other surface uses null so
  // Recharts plots a separate Area per series without zero-rebasing the gaps.
  historical: number | null;
  projected: number | null;
  // Per-person net worth — overlaid alongside the main scoped series on the
  // household scope so the user can see each person's trajectory inside the
  // household total. Historical fills on snapshot dates; projected continues
  // forward through the same projection window as the household line.
  // Bridge point (last historical) populates both so the two halves meet.
  dakotaHistorical: number | null;
  dakotaProjected: number | null;
  nataliaHistorical: number | null;
  nataliaProjected: number | null;
}

function parseUtcDate(s: string): Date {
  return new Date(`${s}T00:00:00Z`);
}

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function addMonths(d: Date, n: number): Date {
  const r = new Date(d);
  r.setUTCMonth(r.getUTCMonth() + n);
  return r;
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Accepts either a numeric ts (the chart's new x-axis value type) or a
// YYYY-MM-DD string (legacy). Falls back to the input verbatim if it
// can't be parsed so the tooltip never renders blank.
function formatTooltipDate(label: string | number | undefined): string {
  if (label == null || label === '') return '';
  if (typeof label === 'number' || /^\d+$/.test(label)) {
    const d = new Date(typeof label === 'number' ? label : Number(label));
    if (Number.isNaN(d.getTime())) return String(label);
    const month = MONTH_LABELS[d.getUTCMonth()] ?? '';
    return `${month} ${d.getUTCFullYear()}`;
  }
  const [y, m] = String(label).split('-');
  const month = MONTH_LABELS[parseInt(m, 10) - 1] ?? '';
  return `${month} ${y}`;
}

interface TooltipItem {
  value?: number | string;
  name?: string;
  color?: string;
  dataKey?: string;
}

// Custom tooltip that collapses the bridge point. The last historical row
// carries both `historical` and `projected` (set to identical values) so the
// two Areas meet without a visible gap — but Recharts' default tooltip then
// shows the same number twice with two series labels. We detect the
// duplicate by exact equality and surface a single "Household" line instead.
//
// Label colors come from the series identity (Household = scoped color,
// Dakota = blue, Natalia = pink); values stay neutral white so the eye
// reads the legend chip + amount as separate fields.
function ChartTooltip({
  active,
  payload,
  label,
  lineColor = 'var(--berg-green)',
}: {
  active?: boolean;
  payload?: TooltipItem[];
  label?: string;
  // Color of the collapsed "Household" entry — driven by the chart's line
  // color so red trajectories surface a red label in the tooltip too.
  lineColor?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const items = payload.filter(
    (p) => p.value != null && p.value !== '' && Number.isFinite(Number(p.value)),
  );
  if (items.length === 0) return null;

  // Dedupe by series identity: at the historical→projected bridge point
  // the last row carries BOTH halves of each persona (same value, two
  // dataKeys), so Recharts hands the tooltip two payload entries per
  // person. Group historical + projected of the same persona and keep
  // the first occurrence so the tooltip reads as one row per identity.
  const identityKey = (dataKey: string | undefined): string => {
    if (dataKey === 'historical' || dataKey === 'projected') return 'household';
    if (dataKey === 'dakotaHistorical' || dataKey === 'dakotaProjected')
      return 'dakota';
    if (dataKey === 'nataliaHistorical' || dataKey === 'nataliaProjected')
      return 'natalia';
    return dataKey ?? '';
  };
  const seen = new Set<string>();
  const deduped: TooltipItem[] = [];
  for (const p of items) {
    const k = identityKey(p.dataKey);
    if (seen.has(k)) continue;
    seen.add(k);
    deduped.push(p);
  }

  // After dedupe, if everything that's left is a single household entry
  // (or all values happen to match the household), surface it under the
  // canonical "Household" label so the scoped-color treatment is
  // consistent across scopes.
  const allEqual =
    deduped.length > 1 &&
    deduped.every((p) => Number(p.value) === Number(deduped[0].value));
  const display = allEqual
    ? [{ ...deduped[0], name: 'Household', color: lineColor }]
    : deduped;
  // Per-series color lookup — falls back to lineColor for the household
  // historical series since its stroke is a gradient URL (unusable as a
  // text color) and to the identity tokens for the per-person overlays.
  const colorFor = (item: TooltipItem): string => {
    if (item.dataKey === 'historical' || item.dataKey === 'projected') return lineColor;
    if (item.dataKey === 'dakotaHistorical' || item.dataKey === 'dakotaProjected')
      return 'var(--berg-dakota)';
    if (item.dataKey === 'nataliaHistorical' || item.dataKey === 'nataliaProjected')
      return 'var(--berg-natalia)';
    return item.color ?? 'var(--berg-text)';
  };
  return (
    <div style={{ ...TOOLTIP_STYLE, padding: '10px 14px' }}>
      <div style={{ ...TOOLTIP_LABEL_STYLE, marginBottom: 6 }}>
        {formatTooltipDate(label)}
      </div>
      {display.map((item, i) => (
        <div
          key={`${item.dataKey ?? item.name ?? i}`}
          style={{ marginTop: i === 0 ? 0 : 2 }}
        >
          <span style={{ color: 'var(--berg-text)', fontWeight: 500 }}>
            {item.name}:
          </span>{' '}
          <span style={{ color: colorFor(item) }}>
            {formatCurrency(Number(item.value), 'USD', 'en-US', 0)}
          </span>
        </div>
      ))}
    </div>
  );
}

type Props = {
  // Current net worth straight from the sheet for all three scopes. The
  // scoped value drives the projection anchor + pins the rightmost
  // historical point; the per-person values pin the dakota/natalia overlay
  // lines so they don't crash to 0 at the synthetic "today" point.
  currentNetWorth: Tri;
  // Year-by-year retirement contribution + employer match for both household
  // members. Drives the contribution side of the forward projection — replaces
  // the older trailing-slope approach so a one-off year doesn't bias the
  // 30-year outlook, and so the projection naturally reflects the schedule
  // the user has planned in their retirement sheet.
  retirement: RetirementData;
  // Household monthly principal portion of debt minimum payments. Each month
  // this much "moves" from liabilities into equity, so it adds to net worth
  // alongside the retirement contribution. Held constant for the projection
  // window — fine for short/medium horizons, less accurate once large debts
  // (e.g., a mortgage) actually pay off. Only the household persona's
  // projection consumes this; per-person projections get 0 (no attribution).
  monthlyDebtPaydown: number;
  // Per-person + household total monthly savings from the budget's
  // "Monthly Savings" line (includes retirement). The chart derives the
  // non-retirement portion by subtracting the current year's retirement
  // contribution and adds whatever remains as a constant monthly
  // contribution — covers brokerage, HYSA, and any other taxable-account
  // savings the user is funding outside of retirement. Passed as a Tri so
  // each persona's projection (household, dakota, natalia) can use its
  // own slice.
  monthlySavings: Tri;
  // Which household member's data to chart. Drives both the historical line
  // (uses snapshot[scope] instead of household) and the projection's
  // retirement contribution sum (single member's contributions instead of
  // both summed). Defaults to household so existing callers keep working.
  scope?: Scope;
  // When true the chart renders inline without its own card wrapper. Used
  // when nesting inside another card (e.g. the net-worth hero).
  flush?: boolean;
  height?: number;
};

export function NetWorthHistoryChart({
  currentNetWorth,
  retirement,
  monthlyDebtPaydown,
  monthlySavings,
  scope = 'household',
  flush = false,
  height = 320,
}: Props) {
  const { snapshots, error } = useNetWorthHistory();

  const data: Point[] = useMemo(() => {
    // 1. Historical: every snapshot ordered by date, one per day.
    const historical = dedupeSnapshotsByDate(snapshots ?? []);

    // 2. Pin "today" to the live sheet value. If the latest snapshot is
    //    older than today (cron lag, missed run, or never ran on this
    //    deploy) we append a synthetic point at today's date so the chart's
    //    rightmost green point reflects the user's current net worth, not
    //    a stale snapshot. If the latest snapshot is dated today we replace
    //    its value with the live sheet figure for the same reason — the
    //    sheet may have been edited after the cron ran.
    //
    //    The synthetic point is display-only — it's intentionally not
    //    included in the trailing-slope calculation above, which only sees
    //    real persisted snapshots so the slope isn't biased by a partial
    //    in-progress month.
    const today = new Date();
    const todayKey = today.toISOString().slice(0, 10);
    const lastSnapshot = historical[historical.length - 1];
    // Synthetic "today" point — populates all three scopes from the live
    // sheet so the household Area AND the per-person overlay lines all pin
    // to today's value (otherwise the non-scoped fields land at 0 and the
    // overlay lines crash to zero at the right edge).
    const scopedCurrent = currentNetWorth[scope];
    const syntheticPoint = (cn: Tri): Snapshot => ({
      date: todayKey,
      source: 'daily' as const,
      household: cn.household,
      dakota: cn.dakota,
      natalia: cn.natalia,
    });
    let displayHistorical = historical;
    if (!lastSnapshot || lastSnapshot.date < todayKey) {
      displayHistorical = [...historical, syntheticPoint(currentNetWorth)];
    } else if (lastSnapshot.date === todayKey && lastSnapshot[scope] !== scopedCurrent) {
      displayHistorical = [
        ...historical.slice(0, -1),
        {
          ...lastSnapshot,
          household: currentNetWorth.household,
          dakota: currentNetWorth.dakota,
          natalia: currentNetWorth.natalia,
        },
      ];
    }

    const anchorDate = displayHistorical[displayHistorical.length - 1]?.date ?? todayKey;
    const anchorDateObj = parseUtcDate(anchorDate);
    const anchorYear = anchorDateObj.getUTCFullYear();

    // Size the projection so historical occupies ~70% of the chart and the
    // projection ~30%. As history accumulates the projection extends with
    // it. Floored at 12 months so the line always shows a year forward,
    // and capped at the retirement sheet's last planned year for whichever
    // persona drives the main Area (household = union of both schedules).
    const HIST_RATIO = 0.7;
    const histMonthBucketCount = new Set(
      displayHistorical.map((s) => s.date.slice(0, 7)),
    ).size;
    const idealProjectionMonths = Math.max(
      12,
      Math.ceil((histMonthBucketCount * (1 - HIST_RATIO)) / HIST_RATIO),
    );
    const SMOOTH_MONTHS = 6;
    const SMOOTH_LOOKBACK = 4;
    const ANNUAL_RETURN = 0.08;
    const monthlyReturn = Math.pow(1 + ANNUAL_RETURN, 1 / 12) - 1;
    const smoothstep = (t: number) => t * t * (3 - 2 * t);

    // Project a single persona forward from the anchor. Reused for the
    // scoped main Area and (on household scope) for the dakota + natalia
    // overlays so each line uses the same model on its own inputs.
    // Returns the projected balance for each month after the anchor, sized
    // by the passed-in projectionMonths so all three personas share an X
    // axis when overlaid.
    const buildProjection = (
      persona: Scope,
      projectionMonths: number,
    ): number[] => {
      // Year → annual retirement contribution (personal + employer match)
      // for this persona. Household sums both members' schedules.
      const annualRetirementByYear = new Map<number, number>();
      const yearsToInclude =
        persona === 'household'
          ? [...retirement.dakota.years, ...retirement.natalia.years]
          : retirement[persona].years;
      for (const y of yearsToInclude) {
        annualRetirementByYear.set(
          y.year,
          (annualRetirementByYear.get(y.year) ?? 0) + y.totals.totalContributionAnnual,
        );
      }

      const anchorValue =
        displayHistorical[displayHistorical.length - 1]?.[persona] ??
        currentNetWorth[persona];
      // Back out the current year's retirement contribution from total
      // monthly savings to isolate the taxable-account portion.
      const todayRetirementMonthly =
        (annualRetirementByYear.get(anchorYear) ?? 0) / 12;
      const monthlyTaxableSavings = Math.max(
        0,
        monthlySavings[persona] - todayRetirementMonthly,
      );
      // Debt paydown is household-only and unattributed — person scopes
      // get 0 so we don't double-count it between the two members.
      const personaDebt = persona === 'household' ? monthlyDebtPaydown : 0;

      // Recent monthly contribution implied by this persona's own
      // historical trajectory. Used for the smoothing blend at the start
      // of the projection so the line eases out of the recent slope.
      let recentMonthlyContribution: number | null = null;
      {
        const latestPerMonth = new Map<string, number>();
        for (const s of displayHistorical) {
          latestPerMonth.set(s.date.slice(0, 7), s[persona]);
        }
        const monthly = [...latestPerMonth.entries()]
          .sort((a, b) => a[0].localeCompare(b[0]))
          .slice(-(SMOOTH_LOOKBACK + 1))
          .map(([, value]) => value);
        if (monthly.length >= 2) {
          let deltaSum = 0;
          for (let i = 1; i < monthly.length; i++) deltaSum += monthly[i] - monthly[i - 1];
          const avgDelta = deltaSum / (monthly.length - 1);
          recentMonthlyContribution = avgDelta - anchorValue * monthlyReturn;
        }
      }

      const values: number[] = [];
      let runningValue = anchorValue;
      for (let i = 1; i <= projectionMonths; i++) {
        const monthDate = addMonths(anchorDateObj, i);
        const annualRetirement =
          annualRetirementByYear.get(monthDate.getUTCFullYear()) ?? 0;
        const modelContribution =
          annualRetirement / 12 + monthlyTaxableSavings + personaDebt;

        let monthlyContribution = modelContribution;
        if (recentMonthlyContribution !== null && i <= SMOOTH_MONTHS) {
          const modelWeight = smoothstep(i / (SMOOTH_MONTHS + 1));
          monthlyContribution =
            (1 - modelWeight) * recentMonthlyContribution +
            modelWeight * modelContribution;
        }

        runningValue = runningValue * (1 + monthlyReturn) + monthlyContribution;
        values.push(runningValue);
      }
      return values;
    };

    // Use the scoped persona's retirement schedule to cap the projection
    // length. On household scope that's the union of both members'
    // schedules, so the per-person overlays are guaranteed not to extend
    // past the main household line.
    const scopedRetirementYears =
      scope === 'household'
        ? [...retirement.dakota.years, ...retirement.natalia.years].map((y) => y.year)
        : retirement[scope].years.map((y) => y.year);
    const lastRetirementYear =
      scopedRetirementYears.length > 0
        ? Math.max(...scopedRetirementYears)
        : today.getUTCFullYear();
    const monthsRemainingInSchedule = Math.max(
      0,
      (lastRetirementYear - anchorYear + 1) * 12 - anchorDateObj.getUTCMonth(),
    );
    const projectionMonths = Math.max(
      0,
      Math.min(idealProjectionMonths, monthsRemainingInSchedule),
    );

    const scopedProjection = buildProjection(scope, projectionMonths);
    // Per-person projections only matter as overlays on the household
    // scope. On dakota / natalia scope the main Area already IS that
    // person's data, so the overlay would just duplicate it.
    const dakotaProjection =
      scope === 'household' ? buildProjection('dakota', projectionMonths) : [];
    const nataliaProjection =
      scope === 'household' ? buildProjection('natalia', projectionMonths) : [];

    const projection: Point[] = [];
    for (let i = 0; i < scopedProjection.length; i++) {
      const monthDate = addMonths(anchorDateObj, i + 1);
      projection.push({
        date: ymd(monthDate),
        ts: monthDate.getTime(),
        historical: null,
        projected: scopedProjection[i],
        // Historical halves of the per-person lines end at the last real
        // snapshot — null on projection months so the solid line stops
        // cleanly and hands off to the dashed projected line.
        dakotaHistorical: null,
        nataliaHistorical: null,
        dakotaProjected: scope === 'household' ? dakotaProjection[i] : null,
        nataliaProjected: scope === 'household' ? nataliaProjection[i] : null,
      });
    }

    // Bridge the two series: the last historical point also carries the
    // projected value at the anchor so both Areas (household + each
    // per-person overlay) meet at exactly the same X/Y. Without this,
    // Recharts plots them as two separate points with the same date label
    // and a gap appears between historical and projected.
    const lastIdx = displayHistorical.length - 1;
    const histPoints: Point[] = displayHistorical.map((s, idx) => ({
      date: s.date,
      ts: parseUtcDate(s.date).getTime(),
      historical: s[scope],
      projected: idx === lastIdx ? s[scope] : null,
      dakotaHistorical: s.dakota,
      nataliaHistorical: s.natalia,
      dakotaProjected: idx === lastIdx && scope === 'household' ? s.dakota : null,
      nataliaProjected: idx === lastIdx && scope === 'household' ? s.natalia : null,
    }));

    return [...histPoints, ...projection];
  }, [snapshots, currentNetWorth, retirement, monthlyDebtPaydown, monthlySavings, scope]);

  const yDomain = useMemo((): [(min: number) => number, (max: number) => number] => {
    // Pad ~8% above and below the data range so the line uses the chart
    // canvas instead of hugging the top or bottom edge. Negative minima
    // get the same 8% headroom (away from zero) so an underwater net-worth
    // line doesn't clip against the bottom of the canvas.
    return [
      (min) =>
        min < 0
          ? Math.floor((min * 1.08) / 1000) * 1000
          : Math.max(0, Math.floor((min * 0.92) / 1000) * 1000),
      (max) => Math.ceil((max * 1.08) / 1000) * 1000,
    ];
  }, []);

  const isEmpty = !error && snapshots !== null && data.length === 0;

  // Where zero sits between the historical line's min and max, as a fraction
  // from the top of its bounding box. Drives the two-color gradient on the
  // historical area: green above zero, red below. Crucially we DO NOT mix in
  // the projection values — the projection extends well past the historical
  // range, so including it would skew the computed zero crossing away from
  // where the historical fill's bounding box actually places y=0. When the
  // historical line never crosses zero, the offset clamps to 0 (all red) or
  // 1 (all green).
  const zeroOffset = useMemo(() => {
    const values: number[] = [];
    for (const d of data) {
      if (d.historical != null) values.push(d.historical);
    }
    if (values.length === 0) return 1;
    const histMin = Math.min(...values);
    const histMax = Math.max(...values);
    if (histMax <= 0) return 0;
    if (histMin >= 0) return 1;
    return histMax / (histMax - histMin);
  }, [data]);
  const zeroOffsetPct = `${zeroOffset * 100}%`;

  // The zero reference line should anchor whenever ANY visible series goes
  // negative — not just the main household line. On household scope a
  // person overlay (e.g. Natalia underwater) dropping below zero deserves
  // a baseline too, so the user can tell at a glance where zero sits.
  // Includes projection values since they're rendered alongside the
  // historical area.
  const showZeroLine = useMemo(() => {
    for (const d of data) {
      const visible: (number | null)[] = [d.historical, d.projected];
      if (scope === 'household') {
        visible.push(
          d.dakotaHistorical,
          d.dakotaProjected,
          d.nataliaHistorical,
          d.nataliaProjected,
        );
      }
      if (visible.some((v) => v != null && v < 0)) return true;
    }
    return false;
  }, [data, scope]);

  // The "above zero" color for the main historical fill + stroke is the
  // person identity color when scoped to Dakota or Natalia, and the default
  // positive-net-worth green when on household scope. Below-zero stays red
  // across all scopes — it's a semantic status indicator, not an identity.
  const aboveZeroColor =
    scope === 'dakota'
      ? 'var(--berg-dakota)'
      : scope === 'natalia'
        ? 'var(--berg-natalia)'
        : 'var(--berg-green)';

  // Same green/red zero-crossing treatment for each per-person overlay so a
  // negative dip on a person's line surfaces in red the way the household
  // line does. The offset is computed from HISTORICAL values only — the
  // gradient is applied to the historical Area, whose SVG bounding box is
  // shaped by its own data range, not by the (often much larger) projection
  // values that live in a separate Area. Mixing projection into the
  // calculation pushes the zero-stop down out of the visible bbox, painting
  // the entire below-the-axis portion above the true zero crossing.
  const personOffset = (
    key: 'dakota' | 'natalia',
  ): number => {
    const values: number[] = [];
    for (const d of data) {
      const h = key === 'dakota' ? d.dakotaHistorical : d.nataliaHistorical;
      if (h != null) values.push(h);
    }
    if (values.length === 0) return 1;
    const min = Math.min(...values);
    const max = Math.max(...values);
    if (max <= 0) return 0;
    if (min >= 0) return 1;
    return max / (max - min);
  };
  const dakotaOffset = useMemo(() => personOffset('dakota'), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const nataliaOffset = useMemo(() => personOffset('natalia'), [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tick timestamps anchored at the FIRST of each month spanning the
  // data's time range. Independent of how many data points fall in any
  // given month so the labels stay evenly spaced regardless of snapshot
  // density. minTickGap further thins if too many months would crowd.
  const monthlyTicks = useMemo(() => {
    if (data.length === 0) return [] as number[];
    const minTs = data[0].ts;
    const maxTs = data[data.length - 1].ts;
    const start = new Date(minTs);
    let y = start.getUTCFullYear();
    let m = start.getUTCMonth();
    const ticks: number[] = [];
    // eslint-disable-next-line no-constant-condition -- intentional: breaks when ts > maxTs
    while (true) {
      const ts = Date.UTC(y, m, 1);
      if (ts > maxTs) break;
      if (ts >= minTs) ticks.push(ts);
      m += 1;
      if (m >= 12) {
        y += 1;
        m -= 12;
      }
    }
    return ticks;
  }, [data]);

  // Gradient-stop builders. Inline JSX would otherwise repeat the same
  // three-branch shape (above-only / below-only / crossing) four times —
  // once each for fill + stroke for dakota + natalia. Opacities default to
  // the softer per-person tint (0.28 max) so red below zero matches the
  // identity color above zero in visual weight.
  const fillStops = (color: string, offset: number) => {
    const offsetPct = `${offset * 100}%`;
    if (offset >= 1)
      return (
        <>
          <stop offset="0%" stopColor={color} stopOpacity={0.28} />
          <stop offset="100%" stopColor={color} stopOpacity={0.02} />
        </>
      );
    if (offset <= 0)
      return (
        <>
          <stop offset="0%" stopColor="var(--berg-red)" stopOpacity={0.02} />
          <stop offset="100%" stopColor="var(--berg-red)" stopOpacity={0.28} />
        </>
      );
    return (
      <>
        <stop offset="0%" stopColor={color} stopOpacity={0.28} />
        <stop offset={offsetPct} stopColor={color} stopOpacity={0.02} />
        <stop offset={offsetPct} stopColor="var(--berg-red)" stopOpacity={0.02} />
        <stop offset="100%" stopColor="var(--berg-red)" stopOpacity={0.28} />
      </>
    );
  };
  const strokeStops = (color: string, offset: number) => {
    const offsetPct = `${offset * 100}%`;
    if (offset >= 1) return <stop offset="0%" stopColor={color} />;
    if (offset <= 0) return <stop offset="0%" stopColor="var(--berg-red)" />;
    return (
      <>
        <stop offset={offsetPct} stopColor={color} />
        <stop offset={offsetPct} stopColor="var(--berg-red)" />
      </>
    );
  };

  const Wrapper = ({ children }: { children: ReactNode }) =>
    flush ? <>{children}</> : <div className="card">{children}</div>;

  return (
    <Wrapper>
      {/* Reserve the chart's full height in every state so the card doesn't
          snap taller when the snapshot fetch resolves. Loading / error /
          empty messages just sit centered inside the reserved space. */}
      {error && (
        <div
          className="text-sm text-fg-tertiary flex items-center justify-center"
          style={{ height }}
        >
          Couldn't load history: {error}
        </div>
      )}
      {!error && snapshots === null && (
        <div
          className="text-sm text-fg-tertiary flex items-center justify-center"
          style={{ height }}
        >
          Loading…
        </div>
      )}
      {isEmpty && (
        <div
          className="text-sm text-fg-tertiary flex items-center justify-center text-center px-4"
          style={{ height }}
        >
          No snapshots yet — the chart will populate after the first daily run.
        </div>
      )}
      {!error && snapshots !== null && data.length > 0 && (
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 24, right: 8, bottom: 0, left: 0 }}>
              <defs>
                {/* Fill gradient: above-zero in the scoped identity color
                    (green for household, blue for Dakota, pink for
                    Natalia) with the standard line-to-zero fade, then a
                    hard transition at the zero crossing into a red fade
                    that grows deeper as the line drops further negative.
                    When the line never crosses zero we emit a single-
                    color gradient — two stops at offset 0/1 leave an SVG
                    edge-pixel artifact that paints a sliver of the
                    "other" color at the boundary. */}
                <linearGradient id="grad-nw-historical" x1="0" y1="0" x2="0" y2="1">
                  {zeroOffset >= 1 ? (
                    <>
                      <stop offset="0%" stopColor={aboveZeroColor} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={aboveZeroColor} stopOpacity={0.02} />
                    </>
                  ) : zeroOffset <= 0 ? (
                    <>
                      <stop offset="0%" stopColor="var(--berg-red)" stopOpacity={0.02} />
                      <stop offset="100%" stopColor="var(--berg-red)" stopOpacity={0.45} />
                    </>
                  ) : (
                    <>
                      <stop offset="0%" stopColor={aboveZeroColor} stopOpacity={0.45} />
                      <stop offset={zeroOffsetPct} stopColor={aboveZeroColor} stopOpacity={0.02} />
                      <stop offset={zeroOffsetPct} stopColor="var(--berg-red)" stopOpacity={0.02} />
                      <stop offset="100%" stopColor="var(--berg-red)" stopOpacity={0.45} />
                    </>
                  )}
                </linearGradient>
                {/* Stroke gradient: same zero-crossing hard transition but
                    full opacity on both sides so the line itself reads as
                    scoped-color above / red below. Same edge-stop
                    avoidance as the fill gradient. */}
                <linearGradient id="grad-nw-historical-stroke" x1="0" y1="0" x2="0" y2="1">
                  {zeroOffset >= 1 ? (
                    <stop offset="0%" stopColor={aboveZeroColor} />
                  ) : zeroOffset <= 0 ? (
                    <stop offset="0%" stopColor="var(--berg-red)" />
                  ) : (
                    <>
                      <stop offset={zeroOffsetPct} stopColor={aboveZeroColor} />
                      <stop offset={zeroOffsetPct} stopColor="var(--berg-red)" />
                    </>
                  )}
                </linearGradient>
                <linearGradient id="grad-nw-projected" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--berg-text-tertiary)" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="var(--berg-text-tertiary)" stopOpacity={0.02} />
                </linearGradient>
                {/* Per-person fade gradients — softer than the household
                    fill so the overlap with the green Area below reads as
                    "tinted area" rather than muddy mixing. Each carries
                    the same green/red zero-crossing treatment as the
                    household gradients so a person's line going negative
                    surfaces as red below zero. */}
                <linearGradient id="grad-nw-dakota" x1="0" y1="0" x2="0" y2="1">
                  {fillStops('var(--berg-dakota)', dakotaOffset)}
                </linearGradient>
                <linearGradient id="grad-nw-dakota-stroke" x1="0" y1="0" x2="0" y2="1">
                  {strokeStops('var(--berg-dakota)', dakotaOffset)}
                </linearGradient>
                <linearGradient id="grad-nw-natalia" x1="0" y1="0" x2="0" y2="1">
                  {fillStops('var(--berg-natalia)', nataliaOffset)}
                </linearGradient>
                <linearGradient id="grad-nw-natalia-stroke" x1="0" y1="0" x2="0" y2="1">
                  {strokeStops('var(--berg-natalia)', nataliaOffset)}
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--berg-border)" strokeDasharray="3 3" vertical={false} />
              {/* Anchor a zero line whenever any visible series crosses it.
                  Sits above the grid lines but under the data so the line's
                  green/red transition reads against this neutral baseline. */}
              {showZeroLine && (
                <ReferenceLine
                  y={0}
                  stroke="var(--berg-border-strong)"
                  strokeWidth={1}
                />
              )}
              <XAxis
                dataKey="ts"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                tick={{ fontSize: 11, fill: 'var(--berg-text-tertiary)' }}
                axisLine={false}
                tickLine={false}
                minTickGap={32}
                // Numeric time axis: data points are placed by actual
                // elapsed time, not by their index in the array. Avoids
                // the dense-daily / sparse-monthly distortion where
                // recent backfill rows would compress into a tiny slice
                // while the projection's monthly cadence stretched wide.
                ticks={monthlyTicks}
                interval="preserveStartEnd"
                tickFormatter={(ts: number) => {
                  const d = new Date(ts);
                  const month = MONTH_LABELS[d.getUTCMonth()] ?? '';
                  return `${month} ${d.getUTCFullYear()}`;
                }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--berg-text-tertiary)' }}
                tickFormatter={(v: number) => formatCompactCurrency(v)}
                axisLine={false}
                tickLine={false}
                width={56}
                domain={yDomain}
              />
              <Tooltip
                // Tooltip "Net worth" label color picks up the sign of the
                // hovered value so the entry reads the same way as the line
                // at that point. The narrow ChartTooltip prop shape doesn't
                // line up with Recharts' broad content-callback type, so
                // the cast keeps the signature pinned.
                content={((props: { active?: boolean; payload?: TooltipItem[]; label?: string }) => {
                  const v = props.payload?.[0]?.value;
                  const num = typeof v === 'number' ? v : Number(v);
                  const tooltipColor =
                    Number.isFinite(num) && num < 0
                      ? 'var(--berg-red)'
                      : aboveZeroColor;
                  return <ChartTooltip {...props} lineColor={tooltipColor} />;
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                }) as any}
                cursor={{ stroke: 'var(--berg-border-strong)', strokeWidth: 1 }}
              />
              <Area
                type="monotone"
                dataKey="historical"
                name="Household"
                stroke="url(#grad-nw-historical-stroke)"
                strokeWidth={2}
                fill="url(#grad-nw-historical)"
                connectNulls={false}
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="projected"
                name="Projected"
                stroke={aboveZeroColor}
                strokeWidth={2}
                strokeDasharray="5 4"
                fill="url(#grad-nw-projected)"
                connectNulls={false}
                isAnimationActive={false}
              />
              {/* Per-person overlay areas — only rendered on the household
                  scope, where the green Area shows the combined household
                  total and the overlays let you see each person's
                  trajectory. Each persona renders as a historical / projected
                  pair: solid filled Area for snapshots, dashed unfilled
                  line forward through the projection window so the visual
                  treatment matches the household historical/projected
                  split below it. On a per-person scope the main Area is
                  already that person's data; overlays would just duplicate
                  it. */}
              {scope === 'household' && (
                <Area
                  type="monotone"
                  dataKey="dakotaHistorical"
                  name="Dakota"
                  stroke="url(#grad-nw-dakota-stroke)"
                  strokeWidth={2}
                  fill="url(#grad-nw-dakota)"
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}
              {scope === 'household' && (
                <Area
                  type="monotone"
                  dataKey="dakotaProjected"
                  name="Dakota"
                  stroke="var(--berg-dakota)"
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  fill="none"
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}
              {scope === 'household' && (
                <Area
                  type="monotone"
                  dataKey="nataliaHistorical"
                  name="Natalia"
                  stroke="url(#grad-nw-natalia-stroke)"
                  strokeWidth={2}
                  fill="url(#grad-nw-natalia)"
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}
              {scope === 'household' && (
                <Area
                  type="monotone"
                  dataKey="nataliaProjected"
                  name="Natalia"
                  stroke="var(--berg-natalia)"
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  fill="none"
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Wrapper>
  );
}
